import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "chargeBase", "chargeRate", "chargeAmount",
    "totalSalaryDeductions", "totalEmployerCharges", "netSalary",
    "autoCalculateToggle", "chargeToggle", "chargeCard"
  ]

  static values = {
    chargeTypes: { type: Array, default: [] },
    formulaConfig: { type: Object, default: {} },
    baseSalary: Number,
    recurringBenefits: Object,
    autoCalculate: String,
    disabledCharges: { type: Array, default: [] },
    benefitRules: { type: Object, default: {} },
    // Benefits in kind: provided, not paid — in the gross, not in the net.
    inKindCodes: { type: Array, default: [] }
  }

  connect() {
    this.calculatedCharges = {}
    this.calculateAll()

    // Listen for salary and benefits changes
    const baseSalaryField = document.querySelector('input[name="employee_profile[base_salary]"]')
    if (baseSalaryField) {
      baseSalaryField.addEventListener('input', () => this.calculateAll())
    }

    // Listen for benefit amount changes
    const benefitFields = document.querySelectorAll('input[name*="[recurring_benefits]"][name*="[amount]"]')
    benefitFields.forEach(field => {
      field.addEventListener('input', () => this.calculateAll())
    })

    // Listen for benefit checkbox changes
    const benefitCheckboxes = document.querySelectorAll('input[name*="[recurring_benefits]"][name*="[enabled]"]')
    benefitCheckboxes.forEach(cb => {
      cb.addEventListener('change', () => this.calculateAll())
    })

    const autoCalcField = document.querySelector('#employee_profile_auto_calculate_social_charges')
    if (autoCalcField) {
      autoCalcField.addEventListener('change', () => this.calculateAll())
    }
  }

  calculateAll() {
    if (!this.isAutoCalculateEnabled()) {
      this.clearAllCalculations()
      return
    }

    const baseSalary = this.getBaseSalary()
    const grossSalary = this.getGrossSalary(baseSalary)
    this.calculatedCharges = {}
    this.currentBaseSalary = baseSalary
    this.salaryBases = this.calculateSalaryBases(grossSalary)

    const chargeTypes = this.chargeTypesValue || []
    chargeTypes.forEach(ct => {
      const amount = this.calculateCharge(ct, baseSalary, grossSalary)
      this.calculatedCharges[ct.code] = amount
      this.updateChargeDisplay(ct, baseSalary, grossSalary, amount)
    })

    this.updateTotals(chargeTypes, grossSalary)
  }

  // A charge the profile leaves out counts for 0. The form says it with the
  // box of the charge, the page of a profile with the list it was saved with.
  isChargeEnabled(code) {
    const toggle = this.hasChargeToggleTarget && this.chargeToggleTargets.find(el => el.dataset.chargeCode === code)
    if (toggle) return toggle.checked
    return !(this.disabledChargesValue || []).includes(code)
  }

  // Generic charge calculation dispatcher
  calculateCharge(chargeType, baseSalary, grossSalary) {
    if (!this.isChargeEnabled(chargeType.code)) return 0
    // A private school owes neither the employer's Crédit Foncier nor the FNE
    // (law 90/050); a charge the school does not apply at all counts for 0.
    if (chargeType.exempt || chargeType.disabled) return 0

    switch (chargeType.calculation_type) {
      case 'percentage':
        return this.calculatePercentageCharge(chargeType, baseSalary, grossSalary)
      case 'progressive_bracket':
        return this.calculateProgressiveBracketCharge(chargeType, baseSalary, grossSalary)
      case 'flat_bracket':
        return this.calculateFlatBracketCharge(chargeType, baseSalary, grossSalary)
      case 'percentage_of_other':
        return this.calculatePercentageOfOtherCharge(chargeType, baseSalary, grossSalary)
      default:
        return 0
    }
  }

  calculatePercentageCharge(chargeType, baseSalary, grossSalary) {
    // The base stops at the ceiling of the country or of the school (11/10/2026).
    let base = this.getBaseAmount(chargeType, baseSalary, grossSalary)
    // Never under the floor of the contribution (the minimum wage, for the
    // pension) for an employee on a monthly salary.
    if (chargeType.floor && base > 0 && baseSalary > 0) base = Math.max(base, chargeType.floor)
    if (chargeType.ceiling) base = Math.min(base, chargeType.ceiling)
    if (chargeType.base_rounding > 1) base = Math.floor(base / chargeType.base_rounding) * chargeType.base_rounding
    return this.round(base * (chargeType.rate / 100.0))
  }

  calculateProgressiveBracketCharge(chargeType, baseSalary, grossSalary) {
    const base = this.getBaseAmount(chargeType, baseSalary, grossSalary)
    if (base <= 0) return 0

    // Each bracket taxes the part of the base inside it, as the payslip does:
    // a scale starting above zero or with a gap leaves the rest untaxed; a
    // bracket starting one franc after the one before takes that franc too.
    const brackets = [...(chargeType.brackets || [])].sort((a, b) => a.min_amount - b.min_amount)
    let totalTax = 0
    let previousMax = null

    brackets.forEach(bracket => {
      let lower = bracket.min_amount
      if (previousMax !== null && lower - previousMax === 1) lower = previousMax
      const upper = bracket.max_amount || Infinity
      previousMax = upper
      totalTax += Math.max(Math.min(base, upper) - lower, 0) * (bracket.rate / 100.0)
    })

    return this.round(totalTax)
  }

  // The bracket the base falls in: the last one starting at or under it,
  // the base taken in whole francs, as the payslip does (14/10/2026).
  calculateFlatBracketCharge(chargeType, baseSalary, grossSalary) {
    const base = Math.round(this.getBaseAmount(chargeType, baseSalary, grossSalary))
    const brackets = [...(chargeType.brackets || [])].sort((a, b) => a.min_amount - b.min_amount)
    const bracket = brackets.filter(b => b.min_amount <= base).pop()

    return bracket ? this.round(bracket.rate) : 0
  }

  calculatePercentageOfOtherCharge(chargeType, baseSalary, grossSalary) {
    const refCode = chargeType.reference_charge_code
    if (!refCode) return 0

    // Ensure the referenced charge is calculated first
    if (this.calculatedCharges[refCode] === undefined) {
      const refChargeType = (this.chargeTypesValue || []).find(ct => ct.code === refCode)
      if (refChargeType) {
        this.calculatedCharges[refCode] = this.calculateCharge(refChargeType, baseSalary, grossSalary)
      }
    }

    const refAmount = this.calculatedCharges[refCode] || 0
    return this.round(refAmount * (chargeType.rate / 100.0))
  }

  // The contributory and the taxable salary of the gross, by the rules of
  // the school and of its country (Payrolls::SalaryBases, server side): each
  // pay element counts for a share of its amount; a benefit in kind is
  // valued at a rate of the intermediate taxable gross, never above its real
  // amount, and the CNPS counts what is over that ceiling, or the real amount.
  calculateSalaryBases(grossSalary) {
    const rules = this.benefitRulesValue || {}
    const share = (code, name) => {
      const value = (rules[code] || {})[name]
      return value === undefined || value === null || value === '' ? 100 : parseFloat(value)
    }
    const rate = code => parseFloat((rules[code] || {}).fiscal_rate) || 0
    const amounts = Object.entries(this.getBenefitAmounts()).filter(([, amount]) => amount > 0)
    const outside = grossSalary - amounts.reduce((sum, [, amount]) => sum + amount, 0)
    const plain = amounts.filter(([code]) => !rate(code))
    const valued = amounts.filter(([code]) => rate(code))
    const intermediate = outside + plain.reduce((sum, [code, amount]) => sum + amount * share(code, 'taxable') / 100, 0)
    let contributory = outside + plain.reduce((sum, [code, amount]) => sum + amount * share(code, 'contributory') / 100, 0)
    let taxable = intermediate
    valued.forEach(([code, amount]) => {
      const ceiling = Math.max(intermediate, 0) * rate(code) / 100
      taxable += Math.min(amount, ceiling) * share(code, 'taxable') / 100
      // The most favourable valuation (18/10/2026): the larger of the real
      // amount and the scale in the contributory, as Payrolls::SalaryBases.
      const rule = rules[code] || {}
      let counted
      if (rule.optimized === true) counted = Math.max(amount, ceiling)
      else if (rule.contributory_valuation === 'real') counted = amount
      else counted = Math.max(amount - ceiling, 0)
      contributory += counted * share(code, 'contributory') / 100
    })
    return { contributory: Math.max(contributory, 0), taxable: Math.max(taxable, 0) }
  }

  // Amount of each benefit counted in the gross, by code.
  getBenefitAmounts() {
    const amounts = {}
    const saved = this.element.dataset.payrollCalculatorRecurringBenefitsValue
    if (saved) {
      try {
        Object.entries(JSON.parse(saved)).forEach(([code, amount]) => {
          if (amount && amount > 0) amounts[code] = parseFloat(amount)
        })
        return amounts
      } catch (e) { /* fallback to form fields */ }
    }

    const amountFields = document.querySelectorAll('input[name^="employee_profile[recurring_benefits]["][name$="][amount]"]')
    amountFields.forEach(field => {
      const checkbox = document.querySelector(`input[name="${field.name.replace(/\[amount\]$/, '[enabled]')}"]`)
      if (checkbox && !checkbox.checked) return
      const code = field.name.match(/\[recurring_benefits\]\[([^\]]+)\]/)[1]
      if (field.value) amounts[code] = parseFloat(field.value)
    })
    return amounts
  }

  // What the benefits in kind amount to: taken back on the net.
  getBenefitsInKind() {
    const amounts = this.getBenefitAmounts()
    return this.inKindCodesValue.reduce((total, code) => total + (amounts[code] || 0), 0)
  }

  getBaseAmount(chargeType, baseSalary, grossSalary) {
    const bases = this.salaryBases || { contributory: grossSalary, taxable: grossSalary }
    switch (chargeType.base_on) {
      case 'base_salary':
        return baseSalary
      case 'contributory_salary':
        return bases.contributory
      case 'taxable_salary':
        return bases.taxable
      case 'net_taxable_salary':
        return this.calculateNetTaxableSalary(grossSalary)
      case 'other_charge': {
        const refCode = chargeType.reference_charge_code
        return this.calculatedCharges[refCode] || 0
      }
      case 'gross_salary':
      default:
        return grossSalary
    }
  }

  calculateNetTaxableSalary(grossSalary) {
    const config = this.formulaConfigValue || {}
    const taxableRate = config.taxable_income_rate ?? 0.70
    const deductionRate = config.contributory_deduction_rate ?? 0.042
    const fixedDeduction = config.fixed_monthly_deduction ?? 41667

    const bases = this.salaryBases || { contributory: grossSalary, taxable: grossSalary }
    // A pay under the threshold of the country is exempt (CGI art. 81: the
    // gross; the taxable gross where the school says so).
    const thresholdBase = config.income_tax_exempt_on === 'taxable_salary' ? bases.taxable : grossSalary
    if (thresholdBase < (config.income_tax_exempt_below ?? 0)) return 0
    const startingSalary = config.net_taxable_base === 'taxable_salary' ? bases.taxable : grossSalary
    // The flat-rate professional expenses, capped (CGI art. 34: 4 800 000 a year).
    let professionalExpenses = startingSalary * (1 - taxableRate)
    if (config.professional_expenses_monthly_cap) professionalExpenses = Math.min(professionalExpenses, config.professional_expenses_monthly_cap)
    const taxableGross = startingSalary - professionalExpenses
    // The contributions taken off: the amount of the charge the school named
    // (the PVID really withheld), or a rate of the gross.
    const named = (this.chargeTypesValue || []).find(ct => ct.code === config.contributory_deduction_charge_code)
    const contributoryDeduction = named && named.base_on !== 'net_taxable_salary'
      ? Math.round(this.calculateCharge(named, this.currentBaseSalary || 0, grossSalary))
      : grossSalary * deductionRate
    return this.round(taxableGross - contributoryDeduction - fixedDeduction)
  }

  // Update DOM for a single charge
  updateChargeDisplay(chargeType, baseSalary, grossSalary, amount) {
    const base = this.getBaseAmount(chargeType, baseSalary, grossSalary)
    const displayBase = (grossSalary <= 0 || base <= 0) ? 0 : base

    const baseEl = this.findChargeTarget('chargeBase', chargeType.code)
    const rateEl = this.findChargeTarget('chargeRate', chargeType.code)
    const amountEl = this.findChargeTarget('chargeAmount', chargeType.code)
    const cardEl = this.findChargeTarget('chargeCard', chargeType.code)
    const enabled = this.isChargeEnabled(chargeType.code)

    if (cardEl) cardEl.style.opacity = enabled ? '' : '0.55'
    if (baseEl) {
      baseEl.textContent = `${this.t('js.payroll_calculator.base')}: ${this.formatCurrency(displayBase)}`
    }
    if (rateEl) {
      if (chargeType.calculation_type === 'flat_bracket') {
        rateEl.textContent = this.t('js.payroll_calculator.fixed_amount')
      } else if (chargeType.calculation_type === 'progressive_bracket' && !enabled) {
        rateEl.textContent = `${this.t('js.payroll_calculator.rate')}: 0%`
      } else if (chargeType.calculation_type === 'progressive_bracket') {
        const pct = base > 0 ? ((amount / base) * 100).toFixed(1) : 0
        rateEl.textContent = `${this.t('js.payroll_calculator.rate')}: ${pct}%`
      } else if (chargeType.calculation_type === 'percentage_of_other') {
        rateEl.textContent = `${this.t('js.payroll_calculator.rate')}: ${chargeType.rate}%`
      } else {
        rateEl.textContent = `${this.t('js.payroll_calculator.rate')}: ${chargeType.rate}%`
      }
    }
    if (amountEl) {
      amountEl.textContent = this.formatCurrency(amount)
    }
  }

  findChargeTarget(targetName, code) {
    const targets = this[`${targetName}Targets`]
    if (!targets) return null
    return targets.find(el => el.dataset.chargeCode === code)
  }

  updateTotals(chargeTypes, grossSalary) {
    let totalSalaryDeductions = 0
    let totalEmployerCharges = 0

    chargeTypes.forEach(ct => {
      const amount = this.calculatedCharges[ct.code] || 0
      if (ct.category === 'salary_deduction') {
        totalSalaryDeductions += amount
      } else if (ct.category === 'employer_charge') {
        totalEmployerCharges += amount
      }
    })

    const netSalary = Math.max(grossSalary - totalSalaryDeductions - this.getBenefitsInKind(), 0)

    if (this.hasTotalSalaryDeductionsTarget) {
      this.totalSalaryDeductionsTarget.textContent = this.formatCurrency(totalSalaryDeductions)
    }
    if (this.hasTotalEmployerChargesTarget) {
      this.totalEmployerChargesTarget.textContent = this.formatCurrency(totalEmployerCharges)
    }
    if (this.hasNetSalaryTarget) {
      this.netSalaryTarget.textContent = this.formatCurrency(netSalary)
    }
  }

  // Input helpers
  getBaseSalary() {
    if (this.hasBaseSalaryValue && this.baseSalaryValue) {
      return parseFloat(this.baseSalaryValue) || 0
    }

    const baseSalaryValue = this.element.dataset.payrollCalculatorBaseSalaryValue
    if (baseSalaryValue) {
      return parseFloat(baseSalaryValue) || 0
    }

    const baseSalaryField = document.querySelector('input[name="employee_profile[base_salary]"]')
    return baseSalaryField ? parseFloat(baseSalaryField.value) || 0 : 0
  }

  getGrossSalary(baseSalary) {
    let grossSalary = baseSalary

    // Try data attribute first (details view)
    const recurringBenefitsValue = this.element.dataset.payrollCalculatorRecurringBenefitsValue
    if (recurringBenefitsValue) {
      try {
        const benefits = JSON.parse(recurringBenefitsValue)
        Object.values(benefits).forEach(amount => {
          if (amount && amount > 0) {
            grossSalary += parseFloat(amount)
          }
        })
        return grossSalary
      } catch (e) { /* fallback to form fields */ }
    }

    // Form fields (form view)
    // Every benefit the form shows counts, whatever its code: the list comes
    // from the table of the country (BenefitType), not from this file.
    const amountFields = document.querySelectorAll('input[name^="employee_profile[recurring_benefits]["][name$="][amount]"]')
    amountFields.forEach(benefitField => {
      const checkbox = document.querySelector(`input[name="${benefitField.name.replace(/\[amount\]$/, '[enabled]')}"]`)
      if (checkbox && !checkbox.checked) return
      if (benefitField.value) {
        grossSalary += parseFloat(benefitField.value)
      }
    })

    return grossSalary
  }

  isAutoCalculateEnabled() {
    const autoCalculateValue = this.element.dataset.payrollCalculatorAutoCalculateValue
    if (autoCalculateValue !== undefined) {
      return autoCalculateValue === 'true'
    }

    const toggleField = document.querySelector('#employee_profile_auto_calculate_social_charges')
    return toggleField ? toggleField.checked : false
  }

  clearAllCalculations() {
    const chargeTypes = this.chargeTypesValue || []
    chargeTypes.forEach(ct => {
      const baseEl = this.findChargeTarget('chargeBase', ct.code)
      const rateEl = this.findChargeTarget('chargeRate', ct.code)
      const amountEl = this.findChargeTarget('chargeAmount', ct.code)

      if (baseEl) baseEl.textContent = `${this.t('js.payroll_calculator.base')}: 0 FCFA`
      if (rateEl) rateEl.textContent = `${this.t('js.payroll_calculator.rate')}: 0%`
      if (amountEl) amountEl.textContent = '0 FCFA'
    })

    if (this.hasTotalSalaryDeductionsTarget) {
      this.totalSalaryDeductionsTarget.textContent = '0 FCFA'
    }
    if (this.hasTotalEmployerChargesTarget) {
      this.totalEmployerChargesTarget.textContent = '0 FCFA'
    }
    if (this.hasNetSalaryTarget) {
      this.netSalaryTarget.textContent = this.formatCurrency(Math.max(this.getGrossSalary(this.getBaseSalary()) - this.getBenefitsInKind(), 0))
    }
  }

  toggleAutoCalculate() {
    this.calculateAll()
  }

  // Utilities
  round(value) {
    return Math.round(value * 100) / 100
  }

  formatCurrency(amount) {
    const config = this.formulaConfigValue || {}
    const currencyCode = config.currency_code || 'XAF'

    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  t(key, options = {}) {
    if (typeof window !== "undefined" && typeof window.t === "function") {
      return window.t(key, options)
    }
    const defaults = {
      'js.payroll_calculator.base': 'Base',
      'js.payroll_calculator.rate': 'Taux',
      'js.payroll_calculator.fixed_amount': 'Montant fixe'
    }
    return defaults[key] || key
  }
}
